import { useState, useEffect } from 'react'
import { Box, Typography, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText, CircularProgress } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import PageService from '../../services/PageService'
import { validateProjectURL } from '../../utils/validateForm'

const AddPageModal = ({
  projectID,
  pages,
  sitemap,
  addPageModalOpen,
  setAddPageModalOpen,
  pageURL,
  setPageURL,
  pagesToggler,
  setPagesToggler,
}) => {
  const [createPageLoading, setCreatePageLoading] = useState(false)
  const [pageURLError, setPageURLError] = useState('')
  const [pageName, setPageName] = useState('')
  const [parentPage, setParentPage] = useState('')
  const [parentPageError, setParentPageError] = useState('')

  useEffect(() => {
    if (sitemap && sitemap.length) {
      setParentPage(sitemap[0].url)
    }
  }, [sitemap])

  const handleClose = () => {
    setAddPageModalOpen(false)
  }

  const handlePageNameChange = (e) => {
    setPageName(e.target.value)
  }

  const handleProjectURLChange = (e) => {
    setPageURL(e.target.value)
    if (pageURLError) {
      setPageURLError('')
    }
  }

  const handleParentPageChange = (event) => {
    setParentPage(event.target.value)
    if (parentPageError) {
      setParentPageError('')
    }
  }

  const handleSave = async () => {
    if (!validateProjectURL(pageURL.trim())) {
      setPageURLError('Invalid URL.')
      return
    } else {
      setPageURLError('')
    }

    if (!parentPage) {
      setParentPageError('Select parent page.')
      return
    } else {
      setPageURLError('')
    }

    try {
      setCreatePageLoading(true)
      const parentPageId = pages.find(page => page.url === parentPage).id
      await PageService.createPage(projectID, pageName, pageURL, parentPageId)
      setPageURL('')
      setParentPage('')
      setCreatePageLoading(false)
      setAddPageModalOpen(false)
      setPagesToggler(!pagesToggler)
    } catch (error) {
      if (error?.response?.data?.error === "Page with the same URL already exists.") {
        setPageURLError(error.response.data.error)
      } else {
        setPageURLError('Invalid URL.')
      }
      setCreatePageLoading(false)
      console.log(error)
    }
  }

  return (
    <Dialog open={addPageModalOpen} onClose={handleClose}>
      <Box className="addPageModal">
        <DialogTitle className="addPageModalTitle">Add Page</DialogTitle>
        <DialogContent className="addPageModalContent">
          <Typography variant='body1' mb={2}>
            Specify the page name, URL, and select the parent page
          </Typography>
          <TextField
            className="addPageModalInput"
            label="Page Name"
            type="text"
            fullWidth
            variant="outlined"
            value={pageName}
            onChange={handlePageNameChange}
          />
          <TextField
            label="Page URL *"
            type="url"
            fullWidth
            variant="outlined"
            value={pageURL}
            onChange={handleProjectURLChange}
            error={!!pageURLError}
            helperText={pageURLError + ' '}
          />
          <FormControl fullWidth error={!!parentPageError}>
            <InputLabel error={!!parentPageError}>Parent Page *</InputLabel>
            <Select
              value={parentPage}
              label="Parent Page"
              onChange={handleParentPageChange}
              error={!!parentPageError}
              sx={{
                color: parentPageError ? 'error.main' : 'inherit',
                '& .MuiInputBase-root': {
                  borderColor: parentPageError ? 'error.main' : 'inherit',
                }
              }}
              MenuProps={{
                classes: { paper: "parentPageDropdown" },
                sx: {
                  maxHeight: '50vh'
                },
              }}
            >
              {pages.map((page) => (
                <MenuItem key={page.id} value={page.url} sx={{ wordBreak: 'break-word' }}>
                  {page.url}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{parentPageError}</FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions className="addPageModalActions">
          <Button className="addPageModalButton" onClick={handleClose} variant="outlined" color='secondary'>Cancel</Button>
          {createPageLoading ? (
            <LoadingButton
              loading
              className="addPageModalButton"
              variant="contained"
              color="secondary"
              loadingIndicator={<CircularProgress color="white" size={16} />}
            >
              Save
            </LoadingButton>
          ) : (
            <Button className="addPageModalButton" onClick={handleSave} variant="contained" color="secondary">
              Save
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default AddPageModal